import type { AxiosError } from 'axios';

/**
 * Mapare coduri de eroare backend -> mesaje în română.
 *
 * Ordinea căutării:
 *   1) `error.response.data.code` (când backend-ul expune un cod de aplicație)
 *   2) HTTP status (fallback generic clar)
 *   3) `error.response.data.message` (când vine tradus/informat de la backend)
 *   4) Mesaj generic RO
 */

type BackendErrorBody = {
  code?: string;
  message?: string;
  // Câmpuri opționale folosite pentru mesaje mai precise
  field?: string;
  missingPrereq?: string;
};

const CODE_MESSAGES: Record<string, string> = {
  CLINICAL_PREREQ_MISSING:
    'Lipsesc date clinice obligatorii pentru a continua. Completează pașii anteriori.',
  FLOW_PREREQ_MISSING:
    'Nu se poate muta episodul: lipsesc prerechizite clinice (vezi detaliile din panoul de flux).',
  FLOW_ROLE_NOT_ALLOWED: 'Rolul tău nu poate efectua această acțiune în fluxul clinic.',
  FLOW_INVALID_TRANSITION: 'Această trecere de fază nu este permisă din starea curentă.',
  FLOW_STEP_LOCKED: 'Pasul este finalizat. Redeschide-l (medicul responsabil) pentru a-l modifica.',
  FLOW_FORM_CLOSED: 'Episodul este închis; nu mai poate fi modificat.',
  VALIDATION_ERROR: 'Datele introduse nu sunt valide. Verifică câmpurile și încearcă din nou.',
  CONFLICT: 'Această înregistrare intră în conflict cu date existente.',
  UNAUTHORIZED: 'Sesiunea a expirat. Te rugăm să te autentifici din nou.',
  FORBIDDEN: 'Nu ai permisiunea necesară pentru această acțiune.',
  NOT_FOUND: 'Resursa solicitată nu a fost găsită.',
  RATE_LIMITED: 'Prea multe cereri. Așteaptă câteva secunde și încearcă din nou.',
  SERVER_ERROR: 'Serverul a întâmpinat o eroare. Încearcă din nou mai târziu.',
};

const STATUS_MESSAGES: Record<number, string> = {
  400: 'Cerere invalidă. Verifică datele trimise și încearcă din nou.',
  401: 'Sesiunea a expirat. Te rugăm să te autentifici din nou.',
  403: 'Nu ai permisiunea necesară pentru această acțiune.',
  404: 'Resursa solicitată nu a fost găsită.',
  409: 'Această înregistrare intră în conflict cu date existente.',
  422: 'Datele introduse nu sunt valide. Verifică câmpurile și încearcă din nou.',
  429: 'Prea multe cereri. Așteaptă câteva secunde și încearcă din nou.',
  500: 'Serverul a întâmpinat o eroare. Încearcă din nou mai târziu.',
  502: 'Serviciul este temporar indisponibil. Încearcă din nou.',
  503: 'Serviciul este temporar indisponibil. Încearcă din nou.',
  504: 'Serviciul nu a răput răspunde la timp. Încearcă din nou.',
};

const FALLBACK_MESSAGE = 'A apărut o eroare. Încearcă din nou.';

/**
 * Mapează o eroare Axios la un mesaj clar, în română, pentru afișare în UI.
 * Nu aruncă excepții — returnează mereu un string.
 *
 * Acceptă `AxiosError<any>` pentru a fi compatibil cu orice interceptor
 * (AxiosError este invariant la parametrul de tip al `data`).
 */
export function mapBackendError(error: AxiosError<any>): string {
  const data = error.response?.data as BackendErrorBody | undefined;
  const status = error.response?.status;

  // 1) Cod specific de aplicație
  if (data?.code && CODE_MESSAGES[data.code]) {
    return CODE_MESSAGES[data.code];
  }

  // 2) Mapare după HTTP status
  if (status !== undefined && STATUS_MESSAGES[status]) {
    return STATUS_MESSAGES[status];
  }

  // 3) Mesaj generic de la backend (dacă există și nu e gol)
  if (data?.message && data.message.trim().length > 0) {
    return data.message;
  }

  // 4) Eroare de rețea (fără response)
  if (!error.response) {
    return 'Nu s-a putut contacta serverul. Verifică conexiunea la internet.';
  }

  // 5) Ultimul fallback
  return FALLBACK_MESSAGE;
}

/**
 * Generează o cheie stabilă pentru deduplicarea toast-urilor.
 * Același cod + status + endpoint = aceeași eroare din punctul de vedere al UI.
 */
export function errorKey(error: AxiosError<any>): string {
  const data = error.response?.data as BackendErrorBody | undefined;
  const code = data?.code ?? 'nocode';
  const status = error.response?.status ?? 'network';
  const url = error.config?.url ?? 'unknown';
  return `${status}:${code}:${url}`;
}
